import React from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from './msalConfig';
import { validateEmailDomain } from './validateEmailDomain';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute guards content behind authentication and domain validation.
 *
 * Behavior:
 * 1. While auth is in progress, displays a loading state.
 * 2. If the user is not authenticated, triggers a login redirect to Azure AD.
 * 3. If authenticated, verifies the email claim ends with @royalcyber.com.
 * 4. If domain is valid, renders children.
 * 5. If domain is invalid, displays an access denied message.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // Show loading while authentication is in progress
  if (inProgress !== InteractionStatus.None) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <p>Authenticating...</p>
      </div>
    );
  }

  // Redirect unauthenticated users to Azure AD login
  if (!isAuthenticated) {
    instance.loginRedirect(loginRequest);
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <p>Redirecting to login...</p>
      </div>
    );
  }

  // Get the active account or fall back to the first account
  const activeAccount = instance.getActiveAccount() ?? accounts[0];

  // Extract email from account username or idTokenClaims
  const email =
    activeAccount?.username ||
    (activeAccount?.idTokenClaims as Record<string, unknown> | undefined)?.email as string ||
    '';

  // Validate email domain
  if (!validateEmailDomain(email)) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <h1>Access Denied</h1>
        <p>
          Access denied. Only Royal Cyber employees (@royalcyber.com) can access
          this platform.
        </p>
      </div>
    );
  }

  return <>{children}</>;
};
